import React from 'react';
import { Table, Button, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

interface StatTableColumn {
  key: string;
  label: string;
  format?: string; // ".4f", "pct", "pvalue"
}

interface StatTableProps {
  title: string;
  columns: StatTableColumn[];
  rows: Record<string, any>[];
  footnotes?: string[];
  highlightSignificant?: boolean; // highlight rows where p < 0.05
  pValueKey?: string; // which column key contains p-values
}

function formatCell(value: any, format?: string): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value !== 'number') return String(value);

  if (format === 'pvalue') {
    if (value < 0.001) return '<0.001';
    if (value < 0.01) return value.toFixed(4);
    return value.toFixed(3);
  }
  if (format === 'pct') return `${(value * 100).toFixed(1)}%`;
  if (format === '.2f') return value.toFixed(2);
  if (format === '.3f') return value.toFixed(3);
  if (format === '.4f') return value.toFixed(4);
  if (format === '.6f') return value.toFixed(6);
  if (format === 'int') return Math.round(value).toString();
  // Default: smart formatting
  if (Number.isInteger(value)) return value.toString();
  if (Math.abs(value) < 0.001 && value !== 0) return value.toExponential(3);
  return value.toFixed(4);
}

function significanceMarker(p: number): string {
  if (p < 0.001) return ' ***';
  if (p < 0.01) return ' **';
  if (p < 0.05) return ' *';
  return '';
}

const StatTable: React.FC<StatTableProps> = ({
  title,
  columns,
  rows,
  footnotes = [],
  highlightSignificant = true,
  pValueKey,
}) => {
  if (!columns?.length || !rows?.length) {
    return null;
  }

  // Auto-detect p-value column
  const pKey = pValueKey || columns.find(c => /^p[_\-]?val|^p$|^p_value$/i.test(c.key))?.key;

  const antColumns = columns.map(col => ({
    title: col.label,
    dataIndex: col.key,
    key: col.key,
    render: (val: any) => {
      const formatted = formatCell(val, col.format);
      const isPCol = col.key === pKey;
      const isSignificant = isPCol && typeof val === 'number' && val < 0.05;
      return (
        <span
          style={{
            fontFamily: typeof val === 'number' ? "'Courier New', monospace" : undefined,
            fontWeight: isSignificant ? 600 : undefined,
            color: isSignificant ? 'var(--danger, #c62828)' : undefined,
          }}
        >
          {formatted}
          {isPCol && typeof val === 'number' && (
            <span style={{ color: '#c62828', fontSize: 11 }}>{significanceMarker(val)}</span>
          )}
        </span>
      );
    },
  }));

  const dataSource = rows.map((row, i) => ({ ...row, _key: `row-${i}` } as Record<string, any>));

  const handleExportCSV = () => {
    const header = columns.map(c => c.label).join(',');
    const csvRows = rows.map(row =>
      columns.map(c => {
        const v = row[c.key];
        if (v === null || v === undefined) return '';
        return typeof v === 'string' && v.includes(',') ? `"${v}"` : String(v);
      }).join(',')
    );
    const csv = [header, ...csvRows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card-trendata" style={{ padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div className="title-bar">
          <h3 style={{ fontSize: 15, fontWeight: 600, color: '#061b31', margin: 0 }}>{title}</h3>
        </div>
        <Tooltip title="导出 CSV">
          <Button size="small" icon={<DownloadOutlined />} onClick={handleExportCSV} />
        </Tooltip>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <Table
          columns={antColumns}
          dataSource={dataSource}
          rowKey="_key"
          pagination={false}
          size="small"
          bordered
          rowClassName={(record) => {
            if (!highlightSignificant || !pKey) return '';
            const p = record[pKey];
            return typeof p === 'number' && p < 0.05 ? 'stat-row-significant' : '';
          }}
          style={{ fontSize: 13 }}
          scroll={{ x: columns.length > 5 ? 'max-content' : undefined }}
        />
      </div>
      {footnotes.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#999', lineHeight: 1.6 }}>
          {footnotes.map((fn, i) => (
            <div key={i}>{fn}</div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StatTable;
